/**
 * ResponsibilitiesPanel — the actual list view shown on To Do and
 * Client Cockpit. Wraps the status endpoint with a month switcher, a
 * manual-complete toggle for untracked items, and a "Responsibilities"
 * button that opens the edit modal.
 *
 * Props:
 *   companyId       — the client we're rendering for
 *   scope           — "client" (To Do) | "accountant" (Client Cockpit)
 *   emptyStateHint  — copy for the "No items assigned yet" state
 *   returnLabel     — label for the breadcrumb back link ("Back to …")
 *   returnPath      — path to return to when clicking area links
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useMoneyFmt } from "@/lib/company";
import { toast } from "sonner";
import {
  CheckCircle2, ChevronLeft, ChevronRight, Loader2, ExternalLink,
  Circle, Clock, RefreshCw, Sliders, ChevronDown,
} from "lucide-react";
import ResponsibilitiesModal from "@/components/ResponsibilitiesModal";
import ReorderAlertsTile from "@/components/ReorderAlertsTile";
import ReconciliationAccountsTile from "@/components/ReconciliationAccountsTile";
import MonthCloseChecklistTile from "@/components/MonthCloseChecklistTile";
import OverdueInvoicesTile from "@/components/OverdueInvoicesTile";
import OverdueBillsTile from "@/components/OverdueBillsTile";
import SalesTaxTile from "@/components/SalesTaxTile";
import PayrollLiabilitiesTile from "@/components/PayrollLiabilitiesTile";
import AiAutoCleanupTile from "@/components/AiAutoCleanupTile";
import CheckinItemsTile from "@/components/CheckinItemsTile";
import ChatReview from "@/pages/ChatReview";
import { useUserPref } from "@/hooks/useUserPref";

// Base tone (border + bg + text) per status. Hover / open variants
// live in HOVER_TONES + OPEN_TONES so the color harmony stays intact
// — amber cards get amber shadows, emerald cards get emerald ones, etc.
const STATUS_TONES = {
  done:         "border-emerald-200 bg-emerald-50/40 text-emerald-900",
  in_progress:  "border-amber-200 bg-amber-50/40 text-amber-900",
  not_started:  "border-slate-200 bg-white text-slate-700",
  "n/a":        "border-slate-200 bg-slate-50 text-slate-500",
};

// Hover glow — subtle lift + tone-tinted shadow + a punchier border
// so a CPA can visually feel which row they're about to click.
const HOVER_TONES = {
  done:         "hover:border-emerald-400 hover:shadow-emerald-100 hover:text-emerald-950",
  in_progress:  "hover:border-amber-400   hover:shadow-amber-100   hover:text-amber-950",
  not_started:  "hover:border-slate-400   hover:shadow-slate-200   hover:text-slate-950",
  "n/a":        "hover:border-slate-400   hover:shadow-slate-200   hover:text-slate-700",
};

// Open state — a soft ring in the tone color so the expanded card
// stays visually anchored while the user reads its inline tile.
const OPEN_TONES = {
  done:         "ring-1 ring-emerald-300 shadow-md shadow-emerald-100",
  in_progress:  "ring-1 ring-amber-300   shadow-md shadow-amber-100",
  not_started:  "ring-1 ring-slate-300   shadow-md shadow-slate-200",
  "n/a":        "ring-1 ring-slate-300   shadow-md shadow-slate-200",
};

const StatusIcon = ({ status }) =>
  status === "done" ? <CheckCircle2 size={14} className="text-emerald-600" /> :
  status === "in_progress" ? <Clock size={14} className="text-amber-600" /> :
  <Circle size={14} className="text-slate-400" />;

const shiftPeriod = (period, delta) => {
  const [y, m] = period.split("-").map(Number);
  const d = new Date(Date.UTC(y, m - 1 + delta, 1));
  return `${d.getUTCFullYear().toString().padStart(4, "0")}-${(d.getUTCMonth() + 1).toString().padStart(2, "0")}`;
};

const currentPeriod = () => {
  const d = new Date();
  return `${d.getFullYear()}-${(d.getMonth() + 1).toString().padStart(2, "0")}`;
};

const monthLabel = (period) => {
  const [y, m] = period.split("-").map(Number);
  return new Date(y, m - 1, 1).toLocaleString("default", { month: "long", year: "numeric" });
};

export default function ResponsibilitiesPanel({
  companyId,
  scope,
  emptyStateHint = "No responsibilities have been set for you yet.",
  returnLabel,
  returnPath,
  preamble = null,
  filter = null,
  onClearFilter,
}) {
  const fmtMoney = useMoneyFmt();
  const [period, setPeriod] = useState(currentPeriod());
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(false);
  // Items whose inline dropdown is expanded (e.g. Monitoring Inventory
  // shows the ReorderAlertsTile inside its row instead of navigating
  // away to the Dashboard).
  const [expanded, setExpanded] = useState(new Set());
  const toggleExpanded = (key) =>
    setExpanded(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });

  // Reviewing-Transactions mode toggle — persisted per user + company
  // via /api/users/me/prefs so the choice follows the CPA across
  // devices (laptop / iPad). Falls back to legacy localStorage for a
  // one-time seamless migration.
  const reviewModeKey = `reviewMode.${companyId || "_"}`;
  const [reviewMode, setReviewMode] = useUserPref(
    reviewModeKey, "chat", { localFallback: reviewModeKey });

  const load = useCallback(async () => {
    if (!companyId) return;
    setBusy(true);
    try {
      const r = await api.get(`/companies/${companyId}/responsibilities/status`, {
        params: { period, scope },
      });
      setData(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load responsibilities.");
    } finally {
      setBusy(false);
    }
  }, [companyId, period, scope]);

  useEffect(() => { load(); }, [load]);

  const toggleComplete = async (item) => {
    // Special case: "Issuing Payroll" is tracked (cadence-driven from
    // `payroll_frequency` + last run timestamp) but we still want a
    // one-click "just ran payroll" affordance on the row for CPAs
    // whose clients use Gusto/ADP externally. Route to a dedicated
    // endpoint that stamps `companies.payroll_state.last_run_at`.
    if (item.key === "issuing_payroll") {
      try {
        await api.post(`/companies/${companyId}/payroll/mark-run`, {});
        toast.success("Marked payroll run complete");
        await load();
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Failed.");
      }
      return;
    }
    if (item.tracked) return;
    try {
      await api.post(`/companies/${companyId}/responsibilities/complete`, {
        item_key: item.key,
        period,
        completed: !item.manual_complete,
      });
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed.");
    }
  };

  const isCurrent = data?.is_current !== false;
  const allItems = data?.items || [];
  const cleanupOnly = filter === "cleanup";
  const items = cleanupOnly ? allItems.filter(i => i.variant === "cleanup") : allItems;

  // Build a link href for the "Open →" affordance. If we have a
  // returnPath, tag it in the query so the target page can render a
  // "← Back to …" breadcrumb. Handles hash fragments correctly —
  // query params must live BEFORE the `#…` so the anchor scroll still
  // fires when the browser lands on the page.
  const buildOpenHref = (base) => {
    if (!base) return "#";
    if (!returnPath) return base;
    const hashIdx = base.indexOf("#");
    const path = hashIdx >= 0 ? base.slice(0, hashIdx) : base;
    const hash = hashIdx >= 0 ? base.slice(hashIdx) : "";
    const sep = path.includes("?") ? "&" : "?";
    return `${path}${sep}return_to=${encodeURIComponent(returnPath)}&return_label=${encodeURIComponent(returnLabel || "")}${hash}`;
  };

  return (
    <div className="space-y-3" data-testid="responsibilities-panel">
      {/* Header + month switcher */}
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2">
          <button
            onClick={() => setPeriod(shiftPeriod(period, -1))}
            className="p-1 border border-slate-300 rounded hover:bg-slate-50"
            data-testid="resp-panel-prev-month"
            aria-label="Previous month"
          >
            <ChevronLeft size={14} />
          </button>
          <div className="text-sm font-semibold text-slate-900 min-w-[140px] text-center">
            {monthLabel(period)}
            {isCurrent && <span className="ml-1.5 text-[10px] text-slate-400 font-normal">(current)</span>}
          </div>
          <button
            onClick={() => setPeriod(shiftPeriod(period, +1))}
            disabled={period === currentPeriod()}
            className="p-1 border border-slate-300 rounded hover:bg-slate-50 disabled:opacity-40"
            data-testid="resp-panel-next-month"
            aria-label="Next month"
          >
            <ChevronRight size={14} />
          </button>
          {period !== currentPeriod() && (
            <button
              onClick={() => setPeriod(currentPeriod())}
              className="text-[11px] px-2 py-0.5 rounded border border-slate-300 hover:bg-slate-50"
              data-testid="resp-panel-today"
            >
              Today
            </button>
          )}
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={load}
            disabled={busy}
            className="text-[11px] px-2 py-1 rounded border border-slate-300 bg-white hover:bg-slate-50 inline-flex items-center gap-1 disabled:opacity-50"
            data-testid="resp-panel-refresh"
          >
            <RefreshCw size={11} className={busy ? "animate-spin" : ""} /> Refresh
          </button>
          <button
            onClick={() => setEditing(true)}
            className="text-[11px] px-2 py-1 rounded bg-slate-900 text-white hover:bg-slate-800 inline-flex items-center gap-1"
            data-testid="resp-panel-edit"
          >
            <Sliders size={11} /> Responsibilities
          </button>
        </div>
      </div>

      {/* Card group — Monitoring Cash Flow (via preamble) + responsibility
          items share a `.cockpit-cards-group` container so hover / open
          state on any one card dims the rest for focus. See index.css. */}
      <div className="space-y-2 cockpit-cards-group">

      {cleanupOnly && (
        <div className="flex items-center justify-between gap-2 rounded-lg border border-slate-300 bg-slate-50 px-3 py-2 text-xs text-slate-700" data-testid="resp-panel-cleanup-filter">
          <span>Showing <b>clean-up items</b> only — older work you can chip away at your own pace.</span>
          {onClearFilter && (
            <button onClick={onClearFilter} className="font-medium text-slate-900 underline underline-offset-2 hover:text-slate-700" data-testid="resp-panel-show-all">
              Show all items
            </button>
          )}
        </div>
      )}

      {!cleanupOnly && preamble}

      {/* List */}
      {busy && !data ? (
        <div className="py-10 flex items-center justify-center text-slate-400">
          <Loader2 className="animate-spin" size={20} />
        </div>
      ) : items.length === 0 ? (
        <div className="rounded-lg border border-dashed border-slate-300 p-8 text-center">
          <div className="text-sm text-slate-700 font-medium">{emptyStateHint}</div>
          <div className="text-xs text-slate-500 mt-1">
            Click <b>Responsibilities</b> above to assign items.
          </div>
        </div>
      ) : (
        <ul className="space-y-2">
          {items.map(item => {
            const isInventory = item.key === "monitoring_inventory";
            const isReconciling = item.key === "reconciling_accounts";
            const isEomClosing = item.key === "eom_closing";
            const isInvoices = item.key === "following_up_invoices";
            const isBills = item.key === "paying_bills";
            const isSalesTax = item.key === "paying_sales_tax";
            const isPayrollLiab = item.key === "paying_payroll_liabilities";
            const isAiAutoCleanup = item.key === "ai_auto_cleanup";
            const isReviewingTxns = item.key === "reviewing_transactions";
            const isReviewChat = isReviewingTxns && reviewMode === "chat";
            // The 4 Quick Check-in cards — same tile, different bucket.
            const CHECKIN_LABELS = {
              liability_payments: "liability payments",
              checks_no_payee:    "checks",
              receipt_followup:   "receipts",
              irs_compliance:     "compliance items",
              cleanup_liability_payments: "historical liability splits",
              cleanup_receipt_followup:   "historical receipts",
              cleanup_irs_compliance:     "historical compliance items",
              cleanup_uncategorized:      "historical uncategorized transactions",
            };
            const isCheckin = Object.prototype.hasOwnProperty.call(CHECKIN_LABELS, item.key);
            // Cleanup cards are the grey historical siblings — they
            // reuse the same expand-in-place tile but render in a
            // muted slate palette so the CPA can tell backlog work
            // from forward-looking work at a glance.
            const isCleanup = item.variant === "cleanup";
            const isExpandable = isInventory || isReconciling || isEomClosing || isInvoices || isBills || isSalesTax || isPayrollLiab || isAiAutoCleanup || isReviewChat || isCheckin;
            const isOpen = expanded.has(item.key);
            return (
            <li
              key={item.key}
              data-open={isOpen ? "true" : "false"}
              className={[
                "cockpit-card rounded-lg border transition-all duration-200",
                // Subtle lift + brighter shadow on hover (skip when
                // the row is already open — the ring conveys focus
                // and stacking a lift on top gets fidgety).
                !isOpen && "hover:shadow-md hover:-translate-y-0.5",
                // Cleanup rows commit to a grey palette regardless
                // of status — visually distinct from the green
                // forward-looking cards.
                isCleanup
                  ? "border-slate-300 bg-slate-100/70 text-slate-700 hover:border-slate-500 hover:shadow-slate-200 hover:text-slate-900"
                  : (STATUS_TONES[item.status] || STATUS_TONES.not_started),
                !isCleanup && (HOVER_TONES[item.status] || HOVER_TONES.not_started),
                isOpen && !isCleanup && (OPEN_TONES[item.status] || OPEN_TONES.not_started),
                isOpen && isCleanup && "ring-1 ring-slate-400 shadow-md shadow-slate-200",
              ].filter(Boolean).join(" ")}
              data-testid={`resp-item-${item.key}`}
            >
              <div className="p-3 flex items-center gap-3">
              <button
                onClick={() => toggleComplete(item)}
                disabled={item.tracked && item.key !== "issuing_payroll"}
                title={item.key === "issuing_payroll"
                  ? "Click to mark payroll run complete for this cycle"
                  : (item.tracked ? "Status is computed automatically"
                    : (item.manual_complete ? "Uncheck to mark incomplete"
                      : "Mark done for this month"))}
                className={`shrink-0 ${(item.tracked && item.key !== "issuing_payroll") ? "cursor-default" : "cursor-pointer hover:scale-110"} transition`}
                data-testid={`resp-item-${item.key}-toggle`}
              >
                <StatusIcon status={item.status} />
              </button>
              <div className="min-w-0 flex-1">
                <div className="text-sm font-medium truncate">
                  {item.label}
                  {item.assignment === "both" && (
                    <span className="ml-1.5 text-[10px] px-1 py-0.5 rounded bg-slate-200 text-slate-700 font-normal">
                      shared
                    </span>
                  )}
                </div>
                {item.breakdown && item.breakdown.length > 0 ? (
                  isAiAutoCleanup ? (
                    <div className="text-[11px] mt-0.5 opacity-80">
                      {item.detail}
                    </div>
                  ) : isReviewingTxns && reviewMode === "chat" && item.chat_counts ? (
                    // Chat-mode pills — mirror the Chat Review 3-tab layout.
                    <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px]"
                         data-testid={`resp-item-${item.key}-chat-pills`}>
                      <span className="text-slate-700">
                        No Category: <b className="font-mono-num">{item.chat_counts.no_category}</b>
                      </span>
                      <span className="text-slate-700">
                        Transactions: <b className="font-mono-num">{item.chat_counts.transactions}</b>
                      </span>
                      <span className="text-slate-700">
                        Checks: <b className="font-mono-num">{item.chat_counts.checks}</b>
                      </span>
                    </div>
                  ) : (
                    <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px]">
                      {item.breakdown.map(b => (
                        <Link
                          key={b.label}
                          to={buildOpenHref(b.href)}
                          className="text-slate-700 hover:text-slate-900 hover:underline"
                          data-testid={`resp-item-${item.key}-bucket-${b.label.replace(/\s+/g, "-").toLowerCase()}`}
                        >
                          {b.label}: <b className="font-mono-num">{b.is_money ? fmtMoney(b.count) : b.count}</b>
                        </Link>
                      ))}
                    </div>
                  )
                ) : (
                  <div className="text-[11px] mt-0.5 opacity-80">{item.detail}</div>
                )}
                {isReviewingTxns && reviewMode === "checklist" && (
                  <div className="mt-1 text-[11px]">
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); setReviewMode("chat"); }}
                      className="text-indigo-700 hover:text-indigo-900 underline"
                      data-testid={`resp-item-${item.key}-switch-chat`}
                    >
                      Switch to chat mode
                    </button>
                  </div>
                )}
              </div>
              {isExpandable && (item.count ?? 0) >= 0 && (isReconciling || isEomClosing || isSalesTax || isPayrollLiab || isAiAutoCleanup || isReviewChat || isCheckin || item.count > 0) ? (
                <button
                  onClick={() => toggleExpanded(item.key)}
                  className="text-[11px] text-slate-700 hover:text-slate-900 inline-flex items-center gap-1 shrink-0"
                  data-testid={`resp-item-${item.key}-open`}
                >
                  {isOpen ? "Hide" : "Open"}
                  <ChevronDown size={12} className={`transition-transform ${isOpen ? "rotate-180" : ""}`} />
                </button>
              ) : item.area_link ? (
                <Link
                  to={buildOpenHref(item.area_link)}
                  className="text-[11px] text-slate-700 hover:text-slate-900 inline-flex items-center gap-1 shrink-0"
                  data-testid={`resp-item-${item.key}-open`}
                >
                  Open <ExternalLink size={10} />
                </Link>
              ) : null}
              </div>
              {/* Inline dropdowns — mirror the Monitoring Inventory pattern
                  so each expandable row reuses its dedicated tile. */}
              {isInventory && isOpen && (
                <div className="px-3 pb-3" data-testid={`resp-item-${item.key}-expanded`}>
                  <ReorderAlertsTile currentId={companyId} variant="slate" />
                </div>
              )}
              {isReconciling && isOpen && (
                <div className="px-3 pb-3" data-testid={`resp-item-${item.key}-expanded`}>
                  <ReconciliationAccountsTile
                    companyId={companyId}
                    period={period}
                    returnPath={returnPath}
                    returnLabel={returnLabel}
                  />
                </div>
              )}
              {isReviewChat && isOpen && (
                <div className="px-3 pb-3 border-t border-slate-100"
                     data-testid={`resp-item-${item.key}-expanded`}>
                  <div className="flex items-center justify-end gap-4 py-2">
                    <Link
                      to={buildOpenHref("/accounting/review-chat")}
                      className="text-[11px] text-indigo-700 hover:text-indigo-900 underline inline-flex items-center gap-1"
                      data-testid={`resp-item-${item.key}-open-full`}
                    >
                      Open full Review Chat page <ExternalLink size={10} />
                    </Link>
                    <button
                      type="button"
                      onClick={() => {
                        setReviewMode("checklist");
                        // Collapse the expansion — checklist mode
                        // routes away rather than expanding inline.
                        toggleExpanded(item.key);
                      }}
                      className="text-[11px] text-indigo-700 hover:text-indigo-900 underline"
                      data-testid={`resp-item-${item.key}-switch-checklist`}
                    >
                      Switch to checklist mode
                    </button>
                  </div>
                  <ChatReview embedded companyId={companyId} />
                </div>
              )}
              {isEomClosing && isOpen && (
                <div className="px-3 pb-3" data-testid={`resp-item-${item.key}-expanded`}>
                  <MonthCloseChecklistTile
                    companyId={companyId}
                    period={period}
                    returnPath={returnPath}
                    returnLabel={returnLabel}
                  />
                </div>
              )}
              {isInvoices && isOpen && (
                <div className="px-3 pb-3" data-testid={`resp-item-${item.key}-expanded`}>
                  <OverdueInvoicesTile
                    companyId={companyId}
                    returnPath={returnPath}
                    returnLabel={returnLabel}
                  />
                </div>
              )}
              {isBills && isOpen && (
                <div className="px-3 pb-3" data-testid={`resp-item-${item.key}-expanded`}>
                  <OverdueBillsTile
                    companyId={companyId}
                    returnPath={returnPath}
                    returnLabel={returnLabel}
                  />
                </div>
              )}
              {isSalesTax && isOpen && (
                <div className="px-3 pb-3" data-testid={`resp-item-${item.key}-expanded`}>
                  <SalesTaxTile
                    companyId={companyId}
                    period={period}
                    returnPath={returnPath}
                    returnLabel={returnLabel}
                  />
                </div>
              )}
              {isPayrollLiab && isOpen && (
                <div className="px-3 pb-3" data-testid={`resp-item-${item.key}-expanded`}>
                  <PayrollLiabilitiesTile
                    companyId={companyId}
                    returnPath={returnPath}
                    returnLabel={returnLabel}
                  />
                </div>
              )}
              {isAiAutoCleanup && isOpen && (
                <div className="px-3 pb-3" data-testid={`resp-item-${item.key}-expanded`}>
                  <AiAutoCleanupTile
                    companyId={companyId}
                    patterns={item.breakdown || []}
                    onChanged={load}
                  />
                </div>
              )}
              {isCheckin && isOpen && (
                <div className="px-3 pb-3" data-testid={`resp-item-${item.key}-expanded`}>
                  <CheckinItemsTile
                    companyId={companyId}
                    items={item.items || []}
                    bucketLabel={CHECKIN_LABELS[item.key]}
                    variant={isCleanup ? "cleanup" : "checkin"}
                    onItemAnswered={load}
                  />
                </div>
              )}
            </li>
          )})}
        </ul>
      )}
      </div>{/* /cockpit-cards-group */}

      <ResponsibilitiesModal
        companyId={companyId}
        open={editing}
        onClose={() => setEditing(false)}
        onSaved={load}
      />
    </div>
  );
}
